import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Loader2,
  RefreshCcw,
  Gift,
  Users,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/referrals/summary");
      setData(data);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <div className="flex items-start justify-between gap-4 mb-8 animate-in-up flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
            Growth
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
            Referrals &amp; Points
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl">
            Track viral growth and reward power users. Each successful referral
            awards 50 points to both parties.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refs-refresh-btn" className="h-10">
          <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading || !data ? (
        <div className="text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 stagger">
            <Kpi
              testId="kpi-total-refs"
              icon={Users}
              label="Total referrals"
              value={data.total_referrals}
              tone="primary"
            />
            <Kpi
              testId="kpi-total-points"
              icon={Gift}
              label="Points issued"
              value={data.total_points_issued}
            />
          </div>

          {/* Leaderboard */}
          <section className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <TrendingUp className="w-4 h-4 text-primary" />
              <div className="text-sm font-bold">Top referrers</div>
            </div>

            {data.top_referrers.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">
                No points earned yet.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="top-refs-table">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left py-2 font-semibold w-10">#</th>
                    <th className="text-left py-2 font-semibold">User</th>
                    <th className="text-left py-2 font-semibold">Code</th>
                    <th className="text-right py-2 font-semibold">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_referrers.map((r, i) => (
                    <tr key={r.user_id} className="border-t border-border">
                      <td className="py-3 font-bold text-muted-foreground">
                        {i + 1}
                      </td>
                      <td className="py-3">
                        <div className="font-semibold">{r.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {r.email}
                        </div>
                      </td>
                      <td className="py-3 mono text-xs">{r.referral_code}</td>
                      <td className="py-3 text-right">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-accent text-accent-foreground text-xs font-bold">
                          {r.points}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}

const Kpi = ({ label, value, icon: Icon, testId, tone }) => (
  <div data-testid={testId} className="bg-card border border-border rounded-2xl p-6">
    <div
      className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${
        tone === "primary"
          ? "bg-primary text-primary-foreground"
          : "bg-secondary text-secondary-foreground"
      }`}
    >
      <Icon className="w-5 h-5" />
    </div>
    <div className="text-3xl font-extrabold tracking-tight leading-none mb-1.5">
      {value}
    </div>
    <div className="text-sm font-medium text-muted-foreground">{label}</div>
  </div>
);
